<template>
  <!-- Single root element wrapper -->
  <div>
    <!-- Main Modal -->
    <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" @click.self="closeModal">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">All {{ zoneLabel }} Performance</h5>
            <div class="ms-auto me-3">
              <span class="badge bg-info">{{ getDateRangeLabel() }}</span>
            </div>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>

          <div class="modal-body">

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading {{ zoneLabel.toLowerCase() }} data...</p>
            </div>

            <!-- No Data State -->
            <div v-else-if="allMarkets.length === 0" class="text-center py-5">
              <div class="mb-3">
                <i class="bx bx-info-circle font-size-48 text-muted"></i>
              </div>
              <h5 class="text-muted">No {{ zoneLabel }} Data Available</h5>
              <p class="text-muted">There are no {{ zoneLabel.toLowerCase() }} to display for the selected period.</p>
              <p class="text-muted"><small>Zone Performance Items: {{ zonePerformance.length }}</small></p>
            </div>

            <!-- Content when not loading and has data -->
            <div v-else>
              <!-- Summary Cards -->
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="card bg-primary text-white">
                    <div class="card-body">
                      <h6 class="text-white-50">Total {{ zoneLabel }}</h6>
                      <h3 class="text-white mb-0">{{ totalMarkets }}</h3>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-success text-white">
                    <div class="card-body">
                      <h6 class="text-white-50">Total Revenue</h6>
                      <h3 class="text-white mb-0">KES {{ numberFormat(totalRevenue) }}</h3>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-warning text-white">
                    <div class="card-body">
                      <h6 class="text-white-50">Average Revenue</h6>
                      <h3 class="text-white mb-0">KES {{ numberFormat(averageRevenue) }}</h3>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-info text-white">
                    <div class="card-body">
                      <h6 class="text-white-50">Total Transactions</h6>
                      <h3 class="text-white mb-0">{{ numberFormat(totalTransactions) }}</h3>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filters -->
              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="input-group">
                    <span class="input-group-text"><i class="bx bx-search"></i></span>
                    <input
                        type="text"
                        class="form-control"
                        :placeholder="`Search by ${zoneLabel.toLowerCase()} name...`"
                        v-model="searchQuery"
                        @input="filterMarkets"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <select class="form-select" v-model="filterGrowth" @change="filterMarkets">
                    <option value="all">All {{ zoneLabel }}</option>
                    <option value="positive">Growing (Positive)</option>
                    <option value="negative">Declining (Negative)</option>
                    <option value="zero">No Change</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <select class="form-select" v-model="filterRevenue" @change="filterMarkets">
                    <option value="all">All Revenue Ranges</option>
                    <option value="high">High (>1M)</option>
                    <option value="medium">Medium (100K-1M)</option>
                    <option value="low">Low (&lt;100K)</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <button class="btn btn-sm btn-secondary w-100" @click="resetFilters">
                    <i class="bx bx-reset"></i> Reset
                  </button>
                </div>
              </div>

              <!-- Results count -->
              <div class="mb-2">
                <small class="text-muted">Showing {{ filteredMarkets.length }} of {{ allMarkets.length }} {{ zoneLabel.toLowerCase() }}</small>
              </div>

              <!-- Markets Table -->
              <div class="table-responsive" style="max-height: 500px;">
                <table class="table table-hover">
                  <thead class="sticky-top bg-light">
                  <tr>
                    <th @click="sortBy('rank')" style="cursor: pointer;">
                      Rank <i class="bx" :class="getSortIcon('rank')"></i>
                    </th>
                    <th @click="sortBy('zone')" style="cursor: pointer;">
                      {{ zoneLabel }}/Zone <i class="bx" :class="getSortIcon('zone')"></i>
                    </th>
                    <th class="text-end" @click="sortBy('currentRevenue')" style="cursor: pointer;">
                      Current Revenue <i class="bx" :class="getSortIcon('currentRevenue')"></i>
                    </th>
                    <th class="text-end" @click="sortBy('lastYearRevenue')" style="cursor: pointer;">
                      Last Year <i class="bx" :class="getSortIcon('lastYearRevenue')"></i>
                    </th>
                    <th class="text-end" @click="sortBy('growth')" style="cursor: pointer;">
                      Growth % <i class="bx" :class="getSortIcon('growth')"></i>
                    </th>
                    <th class="text-end" @click="sortBy('transactionCount')" style="cursor: pointer;">
                      Transactions <i class="bx" :class="getSortIcon('transactionCount')"></i>
                    </th>
                    <th class="text-end" @click="sortBy('userCount')" style="cursor: pointer;">
                      Users <i class="bx" :class="getSortIcon('userCount')"></i>
                    </th>
                    <th class="text-center">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="market in paginatedMarkets"
                      :key="market.zone"
                      :class="{
                        'table-success': market.growth > 10,
                        'table-warning': market.growth < 0,
                        'table-light': market.zone === '' || market.zone === null
                      }">
                    <td>
                      <span class="badge" :class="getRankBadgeClass(market.rank)">
                        #{{ market.rank }}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div class="fw-bold">{{ market.zone || 'Unassigned' }}</div>
                        <small class="text-muted">{{ market.percentage }}% of total</small>
                      </div>
                    </td>
                    <td class="text-end">
                      <div>
                        <div class="fw-bold">KES {{ numberFormat(market.currentRevenue) }}</div>
                        <small class="text-muted">Avg/User: {{ numberFormat(market.averagePerUser) }}</small>
                      </div>
                    </td>
                    <td class="text-end">KES {{ numberFormat(market.lastYearRevenue) }}</td>
                    <td class="text-end">
                      <div class="d-flex align-items-center justify-content-end">
                        <i class="bx" :class="getGrowthIcon(market.growth)"></i>
                        <span :class="getGrowthClass(market.growth)" class="ms-1">
                          {{ market.growth }}%
                        </span>
                      </div>
                    </td>
                    <td class="text-end">{{ numberFormat(market.transactionCount) }}</td>
                    <td class="text-end">{{ market.userCount }}</td>
                    <td class="text-center">
                      <button class="btn btn-sm btn-primary" @click="viewMarketDetails(market)">
                        <i class="bx bx-show"></i> Details
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div class="row mt-3" v-if="totalPages > 1">
                <div class="col-12">
                  <nav>
                    <ul class="pagination justify-content-center">
                      <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" @click="currentPage = 1" href="javascript:void(0);">First</a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" @click="currentPage--" href="javascript:void(0);">Previous</a>
                      </li>

                      <li class="page-item" v-for="page in visiblePages" :key="page"
                          :class="{ active: page === currentPage }">
                        <a class="page-link" @click="currentPage = page" href="javascript:void(0);">{{ page }}</a>
                      </li>

                      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" @click="currentPage++" href="javascript:void(0);">Next</a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" @click="currentPage = totalPages" href="javascript:void(0);">Last</a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
            <button type="button" class="btn btn-primary" @click="exportMarkets" :disabled="loading">
              <i class="bx bx-download"></i> Export All
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ show: show }" v-if="show"></div>

    <!-- Market Details Modal -->
    <MarketDetailsModal
        v-if="showMarketDetails"
        :show="showMarketDetails"
        :market="selectedMarket"
        :stream-prefix="streamPrefix"
        :zone-label="zoneLabel"
        :date-range="dateRange"
        @close="closeMarketDetails"
    />
  </div>
</template>

<script>
/* eslint-disable */
import MarketDetailsModal from "@/components/markets/MarketDetailsModal.vue";
export default {
  name: "AllMarketsModal",
  components: { MarketDetailsModal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    zonePerformance: {
      type: Array,
      default: () => []
    },
    streamPrefix: {
      type: String,
      default: 'MKT'
    },
    zoneLabel: {
      type: String,
      default: 'Markets'
    },
    dateRange: {
      type: Object,
      default: () => ({ type: 'this_month', startDate: '', endDate: '' })
    }
  },
  data() {
    return {
      allMarkets: [],
      filteredMarkets: [],
      loading: false,
      searchQuery: '',
      filterGrowth: 'all',
      filterRevenue: 'all',
      sortColumn: 'currentRevenue',
      sortDirection: 'desc',
      currentPage: 1,
      itemsPerPage: 20,
      debugMode: true, // Set to false in production

      // Market Details Modal
      showMarketDetails: false,
      selectedMarket: null
    }
  },
  computed: {
    totalMarkets() {
      return this.allMarkets.length;
    },
    totalRevenue() {
      return this.allMarkets.reduce((sum, market) => sum + (market.currentRevenue || 0), 0);
    },
    averageRevenue() {
      return this.totalMarkets > 0 ? Math.round(this.totalRevenue / this.totalMarkets) : 0;
    },
    totalTransactions() {
      return this.allMarkets.reduce((sum, market) => sum + (market.transactionCount || 0), 0);
    },
    paginatedMarkets() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredMarkets.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredMarkets.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    show: {
      handler(newVal) {
        console.log('🎯 AllMarketsModal show prop changed to:', newVal);
        if (newVal) {
          console.log('🎯 Modal is opening, calling loadMarkets...');
          this.loadMarkets();
        } else {
          console.log('🎯 Modal is closing');
        }
      },
      immediate: true
    },
    zonePerformance: {
      handler(newVal, oldVal) {
        console.log('🎯 Zone performance data changed:', {
          oldLength: oldVal ? oldVal.length : 0,
          newLength: newVal ? newVal.length : 0,
          modalVisible: this.show
        });

        if (newVal && newVal.length > 0) {
          console.log('🎯 Processing new zone performance data...');
          console.log('🎯 Sample data:', newVal.slice(0, 2));
          this.processMarkets();
        } else {
          console.log('🎯 No zone performance data to process');
        }
      },
      deep: true,
      immediate: true
    },
    dateRange: {
      handler(newVal, oldVal) {
        if (this.show && newVal &&
            (newVal.type !== oldVal?.type || newVal.startDate !== oldVal?.startDate || newVal.endDate !== oldVal?.endDate)) {
          console.log('🎯 Date range changed, reloading markets data');
          this.loadMarkets();
        }
      },
      deep: true
    }
  },

  created() {
    console.log('🎯 AllMarketsModal created');
    console.log('🎯 Initial props:', {
      show: this.show,
      zonePerformanceLength: this.zonePerformance.length,
      streamPrefix: this.streamPrefix,
      zoneLabel: this.zoneLabel,
      dateRange: this.dateRange
    });
  },

  mounted() {
    console.log('🎯 AllMarketsModal mounted');
    console.log('🎯 Mount state:', {
      show: this.show,
      zonePerformanceLength: this.zonePerformance.length,
      allMarketsLength: this.allMarkets.length,
      dateRange: this.dateRange
    });

    // Process data if available
    if (this.zonePerformance.length > 0) {
      console.log('🎯 Processing markets on mount...');
      this.processMarkets();
    }
  },

  methods: {
    getDateRangeLabel() {
      switch (this.dateRange.type) {
        case 'today':
          return 'Today';
        case 'yesterday':
          return 'Yesterday';
        case 'this_week':
          return 'This Week';
        case 'this_month':
          return 'This Month';
        case 'custom':
          return `${this.dateRange.startDate} to ${this.dateRange.endDate}`;
        default:
          return 'Current Period';
      }
    },

    loadMarkets() {
      console.log('🎯 loadMarkets called');
      console.log('🎯 Zone performance available:', this.zonePerformance.length, 'items');
      console.log('🎯 Date range:', this.dateRange);

      this.loading = true;

      // Add a small delay to show loading state
      setTimeout(() => {
        this.processMarkets();
        this.loading = false;
      }, 100);
    },

    processMarkets() {
      console.log('🎯 processMarkets called');
      console.log('🎯 Input zone performance:', this.zonePerformance.length, 'items');

      if (!this.zonePerformance || this.zonePerformance.length === 0) {
        console.log('🎯 No zone performance data available');
        this.allMarkets = [];
        this.filteredMarkets = [];
        return;
      }

      try {
        // Add rank and calculate additional metrics
        this.allMarkets = this.zonePerformance.map((market, index) => {
          const processedMarket = {
            ...market,
            rank: index + 1,
            averagePerUser: market.userCount > 0 ? Math.round(market.currentRevenue / market.userCount) : 0
          };

          console.log('🎯 Processing market:', {
            zone: market.zone,
            currentRevenue: market.currentRevenue,
            userCount: market.userCount,
            averagePerUser: processedMarket.averagePerUser
          });

          return processedMarket;
        });

        console.log('🎯 Processed markets before sorting:', this.allMarkets.length);

        // Sort by current revenue descending
        this.allMarkets.sort((a, b) => (b.currentRevenue || 0) - (a.currentRevenue || 0));

        // Re-assign ranks after sorting
        this.allMarkets.forEach((market, index) => {
          market.rank = index + 1;
        });

        this.filteredMarkets = [...this.allMarkets];

        console.log('🎯 Final processed markets:', {
          total: this.allMarkets.length,
          filtered: this.filteredMarkets.length,
          sample: this.allMarkets.slice(0, 3).map(m => ({
            zone: m.zone,
            rank: m.rank,
            revenue: m.currentRevenue
          }))
        });

      } catch (error) {
        console.error('🎯 Error processing markets:', error);
        this.allMarkets = [];
        this.filteredMarkets = [];
      }
    },

    filterMarkets() {
      console.log('🎯 filterMarkets called');
      let filtered = [...this.allMarkets];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(market =>
            (market.zone || 'unassigned').toLowerCase().includes(query)
        );
        console.log('🎯 After search filter:', filtered.length);
      }

      // Growth filter
      if (this.filterGrowth !== 'all') {
        if (this.filterGrowth === 'positive') {
          filtered = filtered.filter(market => market.growth > 0);
        } else if (this.filterGrowth === 'negative') {
          filtered = filtered.filter(market => market.growth < 0);
        } else if (this.filterGrowth === 'zero') {
          filtered = filtered.filter(market => market.growth === 0);
        }
        console.log('🎯 After growth filter:', filtered.length);
      }

      // Revenue filter
      if (this.filterRevenue !== 'all') {
        if (this.filterRevenue === 'high') {
          filtered = filtered.filter(market => market.currentRevenue > 1000000);
        } else if (this.filterRevenue === 'medium') {
          filtered = filtered.filter(market =>
              market.currentRevenue >= 100000 && market.currentRevenue <= 1000000
          );
        } else if (this.filterRevenue === 'low') {
          filtered = filtered.filter(market => market.currentRevenue < 100000);
        }
        console.log('🎯 After revenue filter:', filtered.length);
      }

      this.filteredMarkets = filtered;
      this.currentPage = 1; // Reset to first page
    },

    sortBy(column) {
      console.log('🎯 sortBy called with column:', column);

      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = column === 'zone' ? 'asc' : 'desc';
      }

      this.filteredMarkets.sort((a, b) => {
        let aVal = a[column];
        let bVal = b[column];

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'bx-sort';
      }
      return this.sortDirection === 'asc' ? 'bx-sort-up' : 'bx-sort-down';
    },

    resetFilters() {
      console.log('🎯 resetFilters called');
      this.searchQuery = '';
      this.filterGrowth = 'all';
      this.filterRevenue = 'all';
      this.filteredMarkets = [...this.allMarkets];
      this.currentPage = 1;
    },

    getRankBadgeClass(rank) {
      if (rank <= 3) return 'bg-success';
      if (rank <= 10) return 'bg-primary';
      if (rank <= 20) return 'bg-info';
      return 'bg-secondary';
    },

    getGrowthClass(growth) {
      if (growth > 0) return 'text-success';
      if (growth < 0) return 'text-danger';
      return 'text-muted';
    },

    getGrowthIcon(growth) {
      if (growth > 0) return 'bx-up-arrow-alt text-success';
      if (growth < 0) return 'bx-down-arrow-alt text-danger';
      return 'bx-minus text-muted';
    },

    viewMarketDetails(market) {
      console.log('🎯 viewMarketDetails called with market:', market.zone, 'dateRange:', this.dateRange);
      this.selectedMarket = market;
      this.showMarketDetails = true;
    },

    closeMarketDetails() {
      console.log('🎯 closeMarketDetails called');
      this.showMarketDetails = false;
      this.selectedMarket = null;
    },

    closeModal() {
      console.log('🎯 closeModal called');
      this.$emit('close');
    },

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    exportMarkets() {
      console.log('🎯 exportMarkets called');

      let csv = `${this.zoneLabel} Performance Report\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += `Stream: ${this.streamPrefix}\n`;
      csv += `Period: ${this.getDateRangeLabel()}\n\n`;

      // Summary
      csv += 'SUMMARY\n';
      csv += `Total ${this.zoneLabel},${this.totalMarkets}\n`;
      csv += `Total Revenue,${this.totalRevenue}\n`;
      csv += `Average Revenue,${this.averageRevenue}\n`;
      csv += `Total Transactions,${this.totalTransactions}\n\n`;

      // Market Data
      csv += `${this.zoneLabel.toUpperCase()} DETAILS\n`;
      csv += `Rank,Zone/${this.zoneLabel},Current Revenue,Last Year Revenue,Growth %,Transactions,Users,Avg per User\n`;

      this.allMarkets.forEach(market => {
        csv += `${market.rank},"${market.zone || 'Unassigned'}",${market.currentRevenue},${market.lastYearRevenue},${market.growth}%,${market.transactionCount},${market.userCount},${market.averagePerUser}\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.streamPrefix}_${this.zoneLabel.toLowerCase()}_performance_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  }
}
</script>